import React from 'react';
import { format } from 'date-fns';
import { Mission } from '../../types';
import { formatMissionType, formatMissionStatus, formatPriority } from '../../utils/formatUtils';

interface PrintMissionReportProps {
  mission: Mission;
}

const PrintMissionReport: React.FC<PrintMissionReportProps> = ({ mission }) => {
  const formatTimestamp = (date?: Date | string | null) => {
    if (!date) return '--';
    return format(new Date(date), 'HH:mm dd/MM/yyyy');
  };

  return (
    <div className="print-only hidden print:block p-8 text-black bg-white text-sm">
      {/* Header */}
      <div className="text-center border-b-2 border-black pb-4 mb-6">
        <h1 className="text-2xl font-bold">TEAAM HEMS - Mission Report</h1>
        <h2 className="text-xl font-semibold mt-1">{mission.missionNumber}</h2>
        <div className="flex justify-center gap-8 mt-2 text-sm">
          <span>Type: {formatMissionType(mission.missionType)}</span>
          <span>Priority: {formatPriority(mission.priority)}</span>
          <span>Status: {formatMissionStatus(mission.status)}</span>
        </div>
        {mission.isTestTraining && (
          <div className="mt-1 font-bold text-gray-600">*** TEST/TRAINING MISSION ***</div>
        )}
      </div>

      {/* Base & Caller Info */}
      <div className="grid grid-cols-2 gap-8 mb-6">
        <div>
          <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Base Information</h3>
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="font-medium pr-4 py-0.5">Primary Base:</td><td>{mission.primaryBase ? `${mission.primaryBase.name} (${mission.primaryBase.code})` : '--'}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Caller Information</h3>
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="font-medium pr-4 py-0.5">Agency:</td><td>{mission.callerAgency || '--'}</td></tr>
              <tr><td className="font-medium pr-4 py-0.5">Name:</td><td>{mission.callerName || '--'}</td></tr>
              <tr><td className="font-medium pr-4 py-0.5">Phone:</td><td>{mission.callerPhone || '--'}</td></tr>
              <tr><td className="font-medium pr-4 py-0.5">Incident #:</td><td>{mission.callerIncidentNumber || '--'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Location */}
      <div className="mb-6">
        <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Location Information</h3>
        <table className="w-full text-sm">
          <tbody>
            <tr><td className="font-medium pr-4 py-0.5 w-40">Scene Location:</td><td>{mission.sceneLocation || '--'}</td></tr>
            <tr><td className="font-medium pr-4 py-0.5">Pickup Location:</td><td>{mission.pickupLocation || '--'}</td></tr>
            <tr><td className="font-medium pr-4 py-0.5">Destination:</td><td>{mission.destinationLocation || '--'}</td></tr>
            <tr><td className="font-medium pr-4 py-0.5">Coordinates:</td><td>{mission.sceneGpsLat && mission.sceneGpsLong ? `${mission.sceneGpsLat}, ${mission.sceneGpsLong}` : '--'}</td></tr>
            <tr><td className="font-medium pr-4 py-0.5">What3Words:</td><td>{mission.what3words || '--'}</td></tr>
          </tbody>
        </table>
      </div>

      {/* Timestamps */}
      <div className="mb-6">
        <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Mission Timeline</h3>
        <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm">
          <div><span className="font-medium">Received:</span> {formatTimestamp(mission.timeReceived)}</div>
          <div><span className="font-medium">Assigned:</span> {formatTimestamp(mission.timeAssigned)}</div>
          <div><span className="font-medium">Aircraft Notified:</span> {formatTimestamp(mission.timeAircraftNotified)}</div>
          <div><span className="font-medium">Aircraft Launched:</span> {formatTimestamp(mission.timeAircraftLaunched)}</div>
          <div><span className="font-medium">Enroute to Scene:</span> {formatTimestamp(mission.timeEnrouteToScene)}</div>
          <div><span className="font-medium">On Scene:</span> {formatTimestamp(mission.timeOnScene)}</div>
          <div><span className="font-medium">Patient Contact:</span> {formatTimestamp(mission.timePatientContact)}</div>
          <div><span className="font-medium">Transporting:</span> {formatTimestamp(mission.timeTransporting)}</div>
          <div><span className="font-medium">At Destination:</span> {formatTimestamp(mission.timeAtDestination)}</div>
          <div><span className="font-medium">Mission Complete:</span> {formatTimestamp(mission.timeMissionComplete)}</div>
          <div><span className="font-medium">Closed:</span> {formatTimestamp(mission.dateClosed)}</div>
        </div>
      </div>

      {/* Patients */}
      <div className="mb-6">
        <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Patient Information ({mission.patients?.length ?? 0} patients)</h3>
        {mission.patients && mission.patients.length > 0 ? (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-1 pr-4">#</th>
                <th className="text-left py-1 pr-4">Age</th>
                <th className="text-left py-1 pr-4">Gender</th>
                <th className="text-left py-1 pr-4">Weight</th>
                <th className="text-left py-1 pr-4">Triage</th>
                <th className="text-left py-1">Chief Complaint</th>
              </tr>
            </thead>
            <tbody>
              {mission.patients.map(patient => (
                <tr key={patient.id} className="border-b border-gray-200">
                  <td className="py-1 pr-4">{patient.patientNumber}</td>
                  <td className="py-1 pr-4">{patient.age ?? '--'}</td>
                  <td className="py-1 pr-4">{patient.gender || '--'}</td>
                  <td className="py-1 pr-4">{patient.weight ? `${patient.weight}kg` : '--'}</td>
                  <td className="py-1 pr-4">{patient.triagePriority || '--'}</td>
                  <td className="py-1">{patient.chiefComplaint || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">No patients recorded</p>
        )}
      </div>

      {/* Aircraft */}
      <div className="mb-6">
        <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Aircraft & Resources</h3>
        {mission.aircraft && mission.aircraft.length > 0 ? (
          mission.aircraft.map(ac => (
            <div key={ac.id} className="mb-2 text-sm">
              <span className="font-medium">{ac.contractor?.companyName || 'Unknown'}</span>
              {ac.tailNumber && <span> - {ac.tailNumber}</span>}
              {ac.aircraftType && <span> ({ac.aircraftType})</span>}
              {ac.notes && <span> — {ac.notes}</span>}
            </div>
          ))
        ) : (
          <p className="text-gray-500">No aircraft assigned</p>
        )}
      </div>

      {/* Notes */}
      <div className="mb-6">
        <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Notes</h3>
        <div className="space-y-2 text-sm">
          {mission.incidentNotes && <div><span className="font-medium">Incident:</span> {mission.incidentNotes}</div>}
          {mission.patientNotes && <div><span className="font-medium">Patient:</span> {mission.patientNotes}</div>}
          {mission.resourceNotes && <div><span className="font-medium">Resources:</span> {mission.resourceNotes}</div>}
          {mission.communicationsNotes && <div><span className="font-medium">Communications:</span> {mission.communicationsNotes}</div>}
          {mission.weatherNotes && <div><span className="font-medium">Weather:</span> {mission.weatherNotes}</div>}
          {!mission.incidentNotes && !mission.patientNotes && !mission.resourceNotes && !mission.communicationsNotes && !mission.weatherNotes && (
            <p className="text-gray-500">No notes recorded</p>
          )}
        </div>
      </div>

      {/* Comments */}
      {mission.comments && mission.comments.length > 0 && (
        <div className="mb-6">
          <h3 className="font-bold border-b border-gray-400 pb-1 mb-2">Communications Log ({mission.comments.length} entries)</h3>
          <div className="space-y-1 text-sm">
            {mission.comments.map(comment => (
              <div key={comment.id}>
                <span className="font-medium">{format(new Date(comment.timestamp), 'HH:mm dd/MM')}</span>
                {' '}<span className="font-medium">[{comment.dispatcherInitials}]</span>
                {' '}{comment.commentText}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t-2 border-black pt-2 mt-8 text-xs text-gray-500 flex justify-between">
        <span>Printed: {format(new Date(), 'HH:mm dd/MM/yyyy')}</span>
        <span>TEAAM HEMS Dispatch System</span>
        <span>Created by: {mission.creator?.fullName || 'Unknown'}</span>
      </div>
    </div>
  );
};

export default PrintMissionReport;
